import { apiFetch } from "./client.js";
import { getProfile } from "./session.js";
import { STORAGE, DEFAULT_CLIENT_ID } from "./config.js";

// ── clientId resolution ──────────────────────────────────────────────────────
// clientId lives at the top level of the profile (`usersList.clientId` in the
// live app). If it's not there, we fall back to a previously resolved id stored
// from a prior client-details call, then to the configured default.
export function getClientId() {
  const p = getProfile();
  const fromProfile = p?.clientId ?? null;
  if (fromProfile != null) return fromProfile;
  try {
    const stored = localStorage.getItem(STORAGE.clientId);
    if (stored) return stored;
  } catch { /* ignore */ }
  return DEFAULT_CLIENT_ID ?? null;
}

// True when the logged-in user is a client (has a clientId / non-tango type).
export function isClientUser() {
  const p = getProfile();
  if (!p) return false;
  if (p.userType && p.userType !== "tango") return true;
  return getClientId() != null;
}

function rememberClientId(id) {
  if (id == null) return;
  try { localStorage.setItem(STORAGE.clientId, String(id)); } catch { /* storage disabled */ }
}

// ── API #1: client-details ───────────────────────────────────────────────────
// GET /v3/client/client-details/{clientId}. Persists featureConfigs and returns
// the parsed client data. Also surfaces a clientId if the response carries one,
// so a profile lacking clientId can still resolve it here.
export async function fetchClientDetails(clientId = getClientId()) {
  if (clientId == null) throw new Error("No clientId available for client-details");
  // silentAuth: a 401 here must not log the user out — fall back to mock data.
  const json = await apiFetch(`/v3/client/client-details/${clientId}`, { method: "GET", silentAuth: true });
  const data = json?.data ?? null;
  try {
    if (data?.featureConfigs != null) {
      localStorage.setItem(STORAGE.clientDetails, JSON.stringify(data.featureConfigs));
    }
  } catch { /* storage disabled — featureConfigs not persisted */ }
  const resolvedId = data?.clientId ?? data?._id ?? data?.id ?? clientId;
  rememberClientId(resolvedId);
  return { data, clientId: resolvedId };
}

// ── API #2: headerStores_v2 ──────────────────────────────────────────────────
// POST /v3/trafficAnalysis/headerStores_v2 — the store list for the dropdown.
// Response is { code, data: { storesData: [...] } }.
export async function fetchHeaderStores({
  clientId = getClientId(),
  status = ["active"],
  country = [],
  city = [],
  clusters = [],
} = {}) {
  if (clientId == null) throw new Error("No clientId available for headerStores_v2");
  const json = await apiFetch("/v3/trafficAnalysis/headerStores_v2", {
    method: "POST",
    silentAuth: true, // a 401 here must not log the user out — fall back to mock.
    body: JSON.stringify({
      country,
      city,
      clusters,
      clientId: String(clientId),
      status,
    }),
  });
  const list = json?.data?.storesData ?? json?.storesData ?? [];
  return mapStores(list);
}

// ── Combined sequential flow ─────────────────────────────────────────────────
// 1) client-details FIRST  → resolves/persists the client (and clientId).
// 2) THEN headerStores_v2  → with that clientId → returns the store list.
// If no clientId can be resolved at all, returns null (caller falls back).

// A single in-flight load shared across overlapping callers, so triggering the
// flow more than once close together (React StrictMode's double-mount in dev, a
// re-render, or the login + refresh effects racing) makes exactly ONE pair of
// calls instead of N. Cleared when it settles so the NEXT refresh re-fetches.
let loadInFlight = null;

export function loadClientStores() {
  if (loadInFlight) return loadInFlight;
  loadInFlight = _loadClientStores().finally(() => { loadInFlight = null; });
  return loadInFlight;
}

async function _loadClientStores() {
  let clientId = getClientId();
  if (clientId == null) return null;

  // Step 1: client-details first. If it resolves a canonical id, use it.
  try {
    const { clientId: resolved } = await fetchClientDetails(clientId);
    if (resolved != null) clientId = resolved;
  } catch (e) {
    console.warn("client-details failed:", e?.message || e);
  }

  // Step 2: stores, using the (possibly updated) clientId.
  return fetchHeaderStores({ clientId });
}

// Title-case a name for display, leaving already-uppercase tokens (acronyms /
// codes) intact: "hosa road" → "Hosa Road", "RS Puram" → "RS Puram".
function toTitleCase(str) {
  return String(str || "")
    .split(/(\s+)/)
    .map((w) => {
      if (!w.trim()) return w;
      if (w === w.toUpperCase()) return w;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join("");
}

// Map live API stores → a simple { id, name, area } shape for the dropdown.
function mapStores(list) {
  return (Array.isArray(list) ? list : []).map((s, i) => ({
    id: String(s.storeId ?? s._id ?? i),
    name: toTitleCase(s.storeName ?? `Store ${i + 1}`),
    area: toTitleCase(s.city ?? s.cluster ?? s.address ?? ""),
    _raw: s,
  }));
}
